"use client";

import * as Sentry from "@sentry/nextjs";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface TranslationFallbackBoundaryProps {
  children: ReactNode;
  /**
   * Optional fallback when the error is NOT a translation-induced
   * `removeChild` failure. Defaults to `null` (silent — let an outer
   * boundary handle non-translation errors).
   */
  nonTranslationFallback?: ReactNode;
}

interface TranslationFallbackBoundaryState {
  /** True once we've caught a translation-induced removeChild NotFoundError. */
  recoveredFromTranslation: boolean;
  /** True for any other error — we render `nonTranslationFallback`. */
  hasOtherError: boolean;
}

/**
 * Heuristic: is this error the well-known browser-translate × React
 * `removeChild` failure (Sentry CUSTOMER-UI-32 family)?
 *
 * Chrome's translate extension wraps text nodes in `<font>` elements.
 * When React's commit phase tries to remove such a text node, the
 * parent chain has been rewritten and `removeChild` throws
 * `NotFoundError` (DOMException code 8) with a message containing
 * `"removeChild"` and `"not a child of this node"`.
 *
 * We match against:
 * - DOMException with `code === 8` (NOT_FOUND_ERR)
 * - or a message containing both "removeChild" and "not a child"
 *
 * This is a heuristic; we'd rather over-recover from a translation
 * crash than under-recover. Other DOMException-8 errors (e.g.
 * intentional removeChild on a detached node from non-React code) are
 * vanishingly rare in this codebase.
 */
function isTranslationInducedRemoveChildError(error: unknown): boolean {
  if (!error) return false;
  if (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException &&
    error.code === DOMException.NOT_FOUND_ERR
  ) {
    return true;
  }
  if (error instanceof Error && typeof error.message === "string") {
    const msg = error.message;
    if (
      msg.includes("removeChild") &&
      msg.includes("not a child of this node")
    ) {
      return true;
    }
    if (msg.includes("NotFoundError") && msg.includes("removeChild")) {
      return true;
    }
  }
  return false;
}

/**
 * Last-line-of-defense error boundary for browser translation crashes.
 *
 * When a translation extension causes React's commit phase to fail with
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'`, this
 * boundary catches the error and re-renders the same children inside a
 * `translate="no"` container. The new mount has fresh DOM nodes that
 * Chrome's MutationObserver leaves alone, so the page continues to
 * function — albeit in English (or whatever the source language is)
 * for the recovered subtree.
 *
 * The recovery is one-way: once a translation error is recovered, the
 * subtree stays in `translate="no"` mode until the page is reloaded.
 * This is intentional — re-enabling translation would just crash again.
 *
 * For non-translation errors, the boundary forwards to
 * `nonTranslationFallback` (default `null`) so an outer boundary can
 * handle them.
 *
 * Apply at the layout level (public, protected, onboarding) so a single
 * subtree's crash doesn't take down the whole app, but several smaller
 * boundaries can also be nested for finer-grained recovery.
 *
 * Usage:
 *   <TranslationFallbackBoundary>
 *     <PageContent />
 *   </TranslationFallbackBoundary>
 */
export class TranslationFallbackBoundary extends Component<
  TranslationFallbackBoundaryProps,
  TranslationFallbackBoundaryState
> {
  state: TranslationFallbackBoundaryState = {
    recoveredFromTranslation: false,
    hasOtherError: false,
  };

  static getDerivedStateFromError(
    error: unknown
  ): Partial<TranslationFallbackBoundaryState> {
    if (isTranslationInducedRemoveChildError(error)) {
      return { recoveredFromTranslation: true };
    }
    return { hasOtherError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const recovered = isTranslationInducedRemoveChildError(error);
    Sentry.captureException(error, {
      tags: {
        boundary: "TranslationFallbackBoundary",
        recovered: recovered ? "true" : "false",
      },
      extra: { componentStack: errorInfo.componentStack },
    });
    if (recovered) {
      console.warn(
        '[TranslationFallbackBoundary] Recovered from translation-induced render crash; re-rendering subtree with translate="no".'
      );
    } else {
      console.error(
        `[TranslationFallbackBoundary] Non-translation error: ${error.message}`
      );
    }
  }

  render() {
    if (this.state.recoveredFromTranslation) {
      // Re-render the same children inside translate="no" + notranslate.
      // Browser translation extensions skip this subtree on subsequent
      // mutations, so the new mount is stable.
      return (
        <div translate="no" className="notranslate" data-translation-recovered>
          {this.props.children}
        </div>
      );
    }
    if (this.state.hasOtherError) {
      return this.props.nonTranslationFallback ?? null;
    }
    return this.props.children;
  }
}
